import type { CppPartner } from '~/services/LuxLoyalty';

/** Bank details the partner remits to. */
export interface RemittanceBank {
  accountName: string;
  bank: string;
  bsb: string;
  accountNumber: string;
  swiftCode: string;
  currency: string;
}

/** The issuing entity block on the invoice. */
export interface IssuingEntity {
  name: string;
  /** Address lines, rendered one per line. */
  addressLines: Array<string>;
  abn: string;
  /** Where the partner pays. Taken from an issued Luxury Escapes tax invoice, not invented. */
  bank: RemittanceBank;
  /** Where the partner sends remittance advice. */
  remittanceEmail: string;
  paymentTermsDays: number;
}

/**
 * Luxury Escapes' details as they appear on an issued tax invoice (INV-2482).
 *
 * The address and bank details are transcribed from a real issued invoice rather than the
 * LOYAL-2701 ticket text, which says "Lv4 / 68 Clark St" — the issued document says `68 Clarke St`
 * with no level, and a document the partner pays against should match what Finance already sends.
 *
 * This is the ABN the invoice carries: Luxury Escapes is the one issuing it, so its own ABN is
 * required. The partner's is not — they are being billed, not billing.
 */
export const LUXURY_ESCAPES_ENTITY: IssuingEntity = {
  name: 'Luxury Escapes Travel Pty. Ltd.',
  addressLines: ['68 Clarke St', 'SOUTHBANK VIC 3006', 'AUSTRALIA'],
  abn: '16 150 650 927',
  bank: {
    accountName: 'LUXURY ESCAPES TRAVEL PTY LTD',
    bank: 'CITI',
    bsb: '242-000',
    accountNumber: '235449005',
    swiftCode: 'CITIAU2X',
    currency: 'AUD',
  },
  remittanceEmail: 'Accounts.Receivable@luxuryescapes.com',
  paymentTermsDays: 7,
};

/**
 * GST rate used when the partner record carries none, as the percentage itself.
 *
 * Matches the column's own default in the service. It matters because the two ways of being wrong
 * are not symmetric: falling back to 0 on a partner whose rate simply has not reached this client
 * yet would silently *underbill* them, whereas a genuinely GST-free partner sends an explicit `0`,
 * which is honoured rather than replaced.
 */
export const DEFAULT_GST_PERCENTAGE = 10;

/** A billing field the partner record can be missing. */
export type PartnerBillingField = 'legalName' | 'address';

/** Human labels for {@link PartnerBillingField}, used to build the warning. */
const FIELD_LABELS: Record<PartnerBillingField, string> = {
  legalName: 'legal entity name',
  address: 'billing address',
};

/** The resolved "Bill To" block for a partner. */
export interface PartnerBilling {
  /** The partner's legal entity name, falling back to its trading name. */
  legalName: string;
  address: string | null;
  /**
   * GST rate as a fraction, e.g. `0.1` for 10%.
   *
   * The service stores and returns this as the percentage itself (`10` means 10%), so it is
   * divided here once, at the boundary, rather than at each use.
   */
  gstRate: number;
  /** Billing fields the partner record does not carry, for the warning. Empty when complete. */
  missing: Array<PartnerBillingField>;
}

/**
 * Resolves a partner's "Bill To" block for the invoice.
 *
 * Every field comes from the partner record. `legalName` and `address` are nullable on partners
 * created before billing details existed: the legal name falls back to the trading name so the
 * document still addresses someone, while the address stays null so the invoice shows it as
 * outstanding rather than printing something wrong. Both are reported in `missing` so the page can
 * name exactly what needs filling in.
 *
 * The partner's ABN is deliberately absent — Luxury Escapes issues this invoice, so only the
 * issuer's ABN belongs on it.
 *
 * @param partner - The selected partner, or undefined when none is selected yet.
 * @returns The billing block, with any unset fields listed in `missing`.
 */
export function resolvePartnerBilling(partner: CppPartner | undefined): PartnerBilling {
  const legalName = partner?.legalName ?? partner?.name ?? '';
  const address = partner?.address ?? null;

  const missing: Array<PartnerBillingField> = [];
  if (partner && !partner.legalName) {
    missing.push('legalName');
  }
  if (partner && !partner.address) {
    missing.push('address');
  }

  return {
    legalName,
    address,
    gstRate: (partner?.gstPercentage ?? DEFAULT_GST_PERCENTAGE) / 100,
    missing,
  };
}

/**
 * Builds the warning naming which billing details the partner record is missing.
 *
 * @param missing - The unset fields, from {@link resolvePartnerBilling}.
 * @returns The warning, or null when nothing is missing and no warning should show.
 */
export function describeMissingBilling(missing: Array<PartnerBillingField>): string | null {
  if (missing.length === 0) {
    return null;
  }

  const labels = missing.map((field) => FIELD_LABELS[field]);
  const list = labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

  const consequences: Array<string> = [];
  if (missing.includes('legalName')) {
    consequences.push('the invoice is addressed to its trading name');
  }
  if (missing.includes('address')) {
    consequences.push('the address is left blank');
  }

  return `This partner has no ${list} on record, so ${consequences.join(' and ')}. Add the missing details to the partner before sending this invoice.`;
}
